import { ColorPicker } from '@/src/Events/components/ColorPicker';
import { Textarea } from '@/src/tailwindui/textarea';
import { Input } from '@/src/tailwindui/input';
import { Heading, Subheading } from '@/src/tailwindui/heading';
import ImageInput from '@/src/Events/components/ImageInput';
import useOrgBranding from './hooks/useOrgBranding';
import SmallLoader from '@/src/Common/components/SmallLoader';
import { Text } from '@/src/tailwindui/text';
import { Divider } from '@/src/tailwindui/divider';
import Library from '@/src/Library/components/Library';
import { useState } from 'react';
import { FolderIcon } from '@heroicons/react/24/outline';
import { Button } from '@/src/tailwindui/button';
import FeatureFlag from '@/src/Features/components/FeatureFlag';
import { FeatureFlags } from '@/src/Features/lib/constants';

import Editor from 'react-simple-code-editor';
import Prism from 'prismjs';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import 'prismjs/themes/prism.css';

export default function Settings() {
    const {
        isSaving,
        lastSaved,
        error,
        isLoading,
        logoUrl,
        emailSubject,
        emailBody,
        emailFromName,
        textSignature,
        galleryHeaderBackgroundColor,
        galleryHeaderForegroundColor,
        galleryBackgroundColor,
        galleryForegroundColor,
        setLogoUrl,
        setEmailSubject,
        setEmailBody,
        setEmailFromName,
        setTextSignature,
        setGalleryHeaderBackgroundColor,
        setGalleryHeaderForegroundColor,
        setGalleryBackgroundColor,
        setGalleryForegroundColor,
        lastChangesMade,
    } = useOrgBranding();

    const [showLibrary, setShowLibrary] = useState(false);
    const [libraryCallback, setLibraryCallback] = useState<
        ((value: string) => void) | null
    >(null);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <SmallLoader />
            </div>
        );
    }

    console.log('logo url', logoUrl);

    return (
        <>
            <div className="mx-auto max-w-4xl">
                <div className="flex flex-wrap items-center justify-between">
                    <div className="sm:flex-1">
                        <Heading className="text-3xl md:text-4xl plex-sans">
                            Organization Settings
                        </Heading>
                        <div className="mt-4 flex max-w-xl gap-4 text-gray-500 dark:text-gray-400">
                            Configure your organization's default settings for
                            branding, email, and color scheme.
                        </div>
                    </div>
                    <div></div>
                </div>
                {error && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4">
                        {error}
                    </div>
                )}
                <div className="flex flex-row gap-4 items-end h-[50px] justify-end mt-2">
                    <div className="flex flex-col items-end justify-end ">
                        {isSaving && <SmallLoader />}
                        {lastChangesMade &&
                            lastSaved &&
                            lastChangesMade < lastSaved && (
                                <p className="text-sm text-gray-500 italic">
                                    All Changes Saved
                                </p>
                            )}
                    </div>
                </div>
                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                    <div className="space-y-1">
                        <Subheading>Logo</Subheading>
                        <Text>
                            This is the logo of the organization, which is
                            displayed in the upper left of the gallery if a
                            specific logo is not set for the event.
                        </Text>
                    </div>
                    <div className="flex flex-row gap-x-2">
                        <ImageInput
                            image={logoUrl}
                            onImageChange={setLogoUrl}
                            key={logoUrl}
                        />
                        <FeatureFlag flag={FeatureFlags.PE_LIBRARY}>
                            <div>
                                <Button
                                    className="text-sm text-[#000000] light-button-no-transparency dark:text-white plex-sans flex flex-row items-center gap-x-2  p-2 cursor-pointer"
                                    onClick={() => {
                                        const callback = (string: string) => {
                                            setLogoUrl(string);
                                        };
                                        setLibraryCallback(() => callback);
                                        setShowLibrary(true);
                                    }}
                                >
                                    <FolderIcon className="w-5 h-5 mr-2" />
                                    Library
                                </Button>
                            </div>
                        </FeatureFlag>
                    </div>
                </section>
                <FeatureFlag
                    flag={FeatureFlags.PE_BRANDED_MESSAGES}
                    fallback={() => {
                        return (
                            <>
                                <Divider className="my-10" />
                                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                                    <div className="space-y-1">
                                        <Subheading>Email Subject</Subheading>
                                    </div>
                                    <div>
                                        <Input
                                            className="shadow-none monospace-font font-size-18 text-[#000000] dark:text-white "
                                            value={emailSubject}
                                            type="text"
                                            onChange={(e) => {
                                                setEmailSubject(e.target.value);
                                            }}
                                        />
                                    </div>
                                </section>
                                <Divider className="my-10" />
                                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                                    <div className="space-y-1">
                                        <Subheading>From Name</Subheading>
                                    </div>
                                    <div>
                                        <Input
                                            className="shadow-none monospace-font font-size-18 text-[#000000] dark:text-white"
                                            value={emailFromName}
                                            type="text"
                                            onChange={(e) => {
                                                setEmailFromName(
                                                    e.target.value,
                                                );
                                            }}
                                        />
                                    </div>
                                </section>
                                <Divider className="my-10" />

                                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                                    <div className="space-y-1">
                                        <Subheading>Text Signature</Subheading>
                                    </div>
                                    <div>
                                        <Textarea
                                            className="shadow-none monospace-font font-size-22 text-[#000000] dark:text-white"
                                            value={textSignature}
                                            onChange={(e) => {
                                                setTextSignature(
                                                    e.target.value,
                                                );
                                            }}
                                        />
                                    </div>
                                </section>
                                <Divider className="my-10" />
                                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-1">
                                    <div className="space-y-1 flex flex-col">
                                        <Subheading>Email Body</Subheading>
                                        <p className="text-sm/7 plex-sans text-[#333333] dark:text-[#aaaaaa] pt-3 pb-5">
                                            This is an HTML email. To inject a
                                            preview image and it's corresponding
                                            link, place a{' '}
                                            <code className="bg-[#f0f0f0] dark:bg-[#333333] p-2 rounded-md">
                                                *|IMAGEURL|*
                                            </code>{' '}
                                            tag in the email template first in
                                            an{' '}
                                            <code className="bg-[#f0f0f0] dark:bg-[#333333] p-2 rounded-md">
                                                &lt;a&gt;
                                            </code>{' '}
                                            tag, then in an{' '}
                                            <code className="bg-[#f0f0f0] dark:bg-[#333333] p-2 rounded-md">
                                                &lt;img /&gt;
                                            </code>{' '}
                                            tag.
                                        </p>
                                        <Editor
                                            value={emailBody}
                                            onValueChange={(value) => {
                                                setEmailBody(value);
                                            }}
                                            highlight={(code) =>
                                                Prism.highlight(
                                                    code,
                                                    Prism.languages.html,
                                                    'html',
                                                )
                                            }
                                            padding={10}
                                            style={{
                                                fontFamily:
                                                    '"IBM-Plex-Mono", "Fira code", "Fira Mono", monospace',
                                                fontSize: 14,
                                            }}
                                            className="shadow-none monospace-font font-size-18 border border-solid border-[#333333] dark:border-[#ffffff]/50 border-opacity-50 rounded-md p-2 text-[#000000] dark:text-[#FFFFFF] max-h-[500px] min-h-[300px]"
                                        />
                                    </div>
                                </section>
                            </>
                        );
                    }}
                >
                    <></>
                </FeatureFlag>
                <Divider className="my-10" />
                <h3 className="font-medium plex-sans-bold text-xl text-[#000000] dark:text-[#ffffff]">
                    Color Scheme
                </h3>

                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2 mt-2">
                    <div className="space-y-1">
                        <Subheading>Header Background Color</Subheading>
                    </div>
                    <div className="flex items-center gap-2 flex-row">
                        <ColorPicker
                            value={galleryHeaderBackgroundColor}
                            onChange={setGalleryHeaderBackgroundColor}
                        />
                        <p className="text-sm text-[#000000] dark:text-[#ffffff] monospace-font ml-2">
                            {galleryHeaderBackgroundColor}
                        </p>
                    </div>
                </section>
                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2 mt-6">
                    <div className="space-y-1">
                        <Subheading>Header Foreground Color</Subheading>
                    </div>
                    <div>
                        <div className="flex items-center gap-2 flex-row">
                            <ColorPicker
                                value={galleryHeaderForegroundColor}
                                onChange={setGalleryHeaderForegroundColor}
                            />
                            <p className="text-sm text-[#000000] dark:text-[#ffffff] monospace-font ml-2">
                                {galleryHeaderForegroundColor}
                            </p>
                        </div>
                    </div>
                </section>
                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2 mt-6">
                    <div className="space-y-1">
                        <Subheading>Background Color</Subheading>
                    </div>
                    <div className="flex items-center gap-2 flex-row">
                        <ColorPicker
                            value={galleryBackgroundColor || ''}
                            onChange={setGalleryBackgroundColor}
                        />
                        <p className="text-sm text-[#000000] dark:text-[#ffffff] monospace-font ml-2">
                            {galleryBackgroundColor}
                        </p>
                    </div>
                </section>
                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2 mt-6">
                    <div className="space-y-1">
                        <Subheading>Text Color</Subheading>
                    </div>
                    <div className="flex items-center gap-2 flex-row">
                        <ColorPicker
                            value={galleryForegroundColor || ''}
                            onChange={setGalleryForegroundColor}
                        />
                        <p className="text-sm text-[#000000] dark:text-[#ffffff] monospace-font ml-2">
                            {galleryForegroundColor}
                        </p>
                    </div>
                </section>
            </div>
            {showLibrary && (
                <div
                    className="fixed inset-0 bg-black/50 bg-opacity-80 flex justify-center items-center"
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowLibrary(false);
                        setLibraryCallback(null);
                    }}
                >
                    <Library
                        onImageSelected={(value: string) => {
                            console.log('value', value);
                            if (libraryCallback) {
                                libraryCallback(value);
                            }
                            setLibraryCallback(null);
                        }}
                        onClose={(e: any) => {
                            e.stopPropagation();
                            setShowLibrary(false);
                            setLibraryCallback(null);
                        }}
                    />
                </div>
            )}
        </>
    );
}
